import { CardPreviewTileRender } from "./card-preview-tile-render";
import { Roles } from "../../../../constants/roles";
import { SNSLinksUtils } from "../../../links/sns-links-utils";
import type { DetectedLinksMap } from "../../../core/sns-detector";
import { snsPreviewManagerForm } from "../../sns-preview-manager-form";
import { BoardMediaSliderSetup } from "../../../../post/list/board-media-slider-setup";

export class SnsCardPreviewTilesGridRender {
    public static normalizeTileUrl(url: string): string {
        const raw = String(url || "").trim();
        if (!raw) return "";
        const normalized = SNSLinksUtils.normalizeSnsLinks([raw]);
        return normalized[0] || raw;
    }

    public static addSnsTilesGridPreviewToCard(
        cardElement: Element,
        normalizedSnsLinks: string[],
        detectedLinks: DetectedLinksMap,
        maxTiles = 4,
    ): boolean {
        if (!(cardElement instanceof HTMLElement)) return false;

        const boardMediaArea = snsPreviewManagerForm.getBoardMediaArea(cardElement);
        if (!(boardMediaArea instanceof HTMLElement)) return false;

        const requestedMaxTiles = Number.isFinite(maxTiles) ? Math.max(1, Math.floor(maxTiles)) : 4;

        let urls = Array.isArray(normalizedSnsLinks) ? normalizedSnsLinks.map((u) => String(u || "").trim()) : [];
        urls = urls.filter(Boolean);

        if (!urls.length) {
            for (const links of Object.values(detectedLinks || {})) {
                if (!Array.isArray(links) || !links.length) continue;
                for (const linkObj of links) {
                    const value = String(linkObj?.url || "").trim();
                    if (value) urls.push(value);
                }
            }
        }

        const seen = new Set<string>();
        urls = urls.filter((u) => {
            if (seen.has(u)) return false;
            seen.add(u);
            return true;
        });

        urls = urls.slice(0, requestedMaxTiles);

        const existingGrid = snsPreviewManagerForm.getSnsPreviewTilesGrid(boardMediaArea);
        const hasGrid = existingGrid instanceof HTMLElement;

        if (!hasGrid && urls.length < 1) return false;

        const placeholder = snsPreviewManagerForm.getBoardMediaPlaceholder(boardMediaArea);
        if (placeholder) placeholder.remove();

        const grid = hasGrid && existingGrid instanceof HTMLElement ? existingGrid : document.createElement("div");
        if (!hasGrid) {
            grid.className = "sns-preview-tiles--index-grid";
            grid.setAttribute("data-role", Roles.SNS_PREVIEW_TILES_GRID);
            boardMediaArea.appendChild(grid);
        }

        const existingSnsUrls = new Set<string>();
        snsPreviewManagerForm.getTileUrlElements(grid).forEach((el) => {
            const value = this.normalizeTileUrl(el.getAttribute("data-sns-url") || "");
            if (value) existingSnsUrls.add(value);
        });
        snsPreviewManagerForm.getSnsOpenHrefAnchors(grid).forEach((el) => {
            const value = this.normalizeTileUrl(el.getAttribute("href") || "");
            if (value) existingSnsUrls.add(value);
        });

        urls = urls.filter((url) => {
            const normalized = this.normalizeTileUrl(url);
            return Boolean(normalized) && !existingSnsUrls.has(normalized);
        });

        const remaining = Math.max(0, requestedMaxTiles - grid.children.length);
        if (remaining <= 0) {
            grid.setAttribute("data-count", String(grid.children.length));
            BoardMediaSliderSetup.init(boardMediaArea);
            return false;
        }

        let appended = 0;
        for (const url of urls) {
            if (appended >= remaining) break;
            CardPreviewTileRender.renderIndexSnsTilePreview(grid, url);
            const normalized = this.normalizeTileUrl(url);
            if (normalized) existingSnsUrls.add(normalized);
            appended += 1;
        }

        grid.setAttribute("data-count", String(grid.children.length));
        BoardMediaSliderSetup.init(boardMediaArea);
        return appended > 0;
    }
}
